import styled from 'styled-components';
import { ChangeEvent, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import RemoveInputIcon from '../../../../new-components/svg-icons/RemoveInputIcon';
import { SearchInputResult } from '../../../search/components/search-bar/SearchLocationResults';
import { Destination } from '../../types';
import ResultsDropdown from '../search/ResultsDropdown';
import DestinationIcon from './DestinationIcon';
import { logEvent } from '../../../../new-lib/analytics';
import { getPageLocation } from '../../../../helpers/common';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import debounce from '../../../search/utils';
import { useSearch } from '../../../search/hooks/SearchInput';
import {
  setFirstDestination,
  setSecondDestination
} from '../../../../redux/slices/mapSlice';

interface CalculateDistanceInputProps {
  destination: Destination;
  isSahel?: boolean;
}
const CalculateDistanceInput = ({
  destination,
  isSahel
}: CalculateDistanceInputProps) => {
  const { t } = useTranslation('map');

  const { asPath } = useRouter();
  const dispatch = useDispatch();

  const firstDestination = useSelector(
    (state: any) => state.map.firstDestination
  );
  const secondDestination = useSelector(
    (state: any) => state.map.secondDestination
  );

  const compounds = useSelector((state: any) => state.map.compounds);

  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const debouncedResults = useCallback(
    debounce((term: string) => {
      setSearchTerm(term);
    }, 500),
    []
  );

  const {
    searchResults: searchLocations,
    isError,
    isLoading,
    searchTerm,
    setSearchTerm
  } = useSearch({
    searchInputLocation: 'map',
    calculateDstance: true,
    isSahel: isSahel
  });
  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (!dropdownOpen) {
      openDropdown();
    }
    setDebouncedSearchTerm(e.target.value);
  };
  const handleSelectResult = useCallback(
    (selection: SearchInputResult) => {
      setSearchTerm(selection?.name);

      closeDropdown();

      dispatch(
        destination === 'a'
          ? setFirstDestination({
              ...selection,
              northcoastKm: selection?.northcoast_km,
              coordinates: selection?.location_point
            })
          : setSecondDestination({
              ...selection,
              northcoastKm: selection?.northcoast_km,
              coordinates: selection?.location_point
            })
      );
      logEvent.click(
        `map_distance_${destination}`,
        getPageLocation(asPath),
        'map',
        {
          cmpnd_id: selection?.id,
          cmpnd_name: selection?.name
        }
      );
    },
    [compounds]
  );

  const openDropdown = () => {
    setDropdownOpen(true);
  };

  const closeDropdown = () => {
    if (firstDestination && destination === 'a' && searchTerm === '') {
      setSearchTerm(firstDestination?.name);
    }

    if (secondDestination && destination === 'b' && searchTerm === '') {
      setSearchTerm(firstDestination?.name);
    }
    setDropdownOpen(false);
  };

  const removeSelection = () => {
    setDebouncedSearchTerm('');
    closeDropdown();
    dispatch(
      destination === 'a'
        ? setFirstDestination(null)
        : setSecondDestination(null)
    );
  };
  useEffect(() => {
    if (firstDestination && destination === 'a') {
      setDebouncedSearchTerm(firstDestination?.name);
    }

    if (secondDestination && destination === 'b') {
      setDebouncedSearchTerm(secondDestination?.name);
    }

    if (
      (!firstDestination && destination === 'a') ||
      (!secondDestination && destination === 'b')
    ) {
      setDebouncedSearchTerm('');
    }
  }, [firstDestination, secondDestination]);

  useEffect(() => {
    if (debouncedSearchTerm || debouncedSearchTerm === '') {
      debouncedResults(debouncedSearchTerm);
    }
  }, [debouncedSearchTerm, debouncedResults]);

  return (
    <Container>
      <DestinationIcon destination={destination} />

      <div className="input-section">
        <input
          type="text"
          name="search-input"
          placeholder={t(`destination_${destination}`)}
          onChange={e => handleInputChange(e)}
          value={debouncedSearchTerm}
        />

        {debouncedSearchTerm?.length > 0 && (
          <div onClick={removeSelection}>
            <RemoveInputIcon />
          </div>
        )}
      </div>
      {dropdownOpen && (
        <ResultsDropdown
          isLoading={isLoading}
          isError={isError}
          searchResults={searchLocations}
          handleClick={handleSelectResult}
          closeDropdown={closeDropdown}
          destination={destination}
        />
      )}
    </Container>
  );
};

const Container = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  background-color: #f9fafb;
  border-radius: 8px;
  border: 1px solid #d1d4d9;
  padding: 8px 16px;
  gap: 8px;
  .icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 30px;
    height: 30px;
    padding: 10px;
    background-color: #e6eff5;
    border-radius: 360px;
    border: 1px solid #d1d4d9;
    color: #000;
    font-size: 17px;
    font-family: materialBold;
  }

  .input-section {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 4px;

    input {
      font-size: 16px !important;
      font-family: materialRegular;
      background-color: #f9fafb;
      border: none;
      width: 100%;
      outline: none;
      :focus {
        outline: none;
      }
    }

    svg {
      cursor: pointer;
    }
  }

  @media screen and (max-width: ${props =>
      props.theme.breakPoints.mobile.max}) {
    max-width: 45%;
    height: 45px;
    padding: 8px;
    gap: 4px;
    background-color: #fff;
    border: 1px solid #f0f1f3;
    border-radius: 8px;

    .icon {
      width: 24px;
      height: 24px;
    }

    .input-section {
      input {
        max-width: 95%;
      }
    }
  }
`;

export default CalculateDistanceInput;
